import React, { FC, useEffect, useState, useRef } from "react";
import { useEvent, useStore } from "effector-react";
// entities
import { ChatGroups } from "entities/chats/ui/chat-groups";
// shared
import { ChatGrop } from "shared/ui/chat-group";
import { ChatMessage } from "shared/ui/chat-message";
import { BaseInput } from "shared/ui/input/base/input";
import SearchInput from "shared/ui/input/base/search-input";
import { SettingsPanel } from "shared/ui/settings-panel";
import sendIcon from "shared/icons/chat/send.svg";
import sendActiveIcon from "shared/icons/chat/sendActive.svg";
// features
import { ChatModel } from "entities/chats";
import { BarLoader } from "react-spinners";
import { AccountModel } from "entities/account";
import { getAvatarResourcesUrl } from "shared/helpers/get-avatar-resources-url";
import { useChatListWebSocket } from "entities/chats/lib/hooks";
import { useForm } from "react-hook-form";
import { GroupChatGroups } from "entities/chats/ui/group-chat-groups";
import { Message, IndividualChat, GroupChatList } from "entities/chats/types";
import { addMessage } from "entities/chats/model";
import { IChatFeature } from "./type";
import { userLogoutEvent } from "entities/auth";
import pinIcon from "../../shared/icons/chat/pin.svg";
import arrowUpIcon from "../../shared/icons/controls/arrow-back.svg";
import arrowDownIcon from "../../shared/icons/controls/arrow-bottom.svg";

const ChatFeature: FC<IChatFeature> = ({ setVisible }) => {
  const { control } = useForm({
    defaultValues: {
      messageText: "",
    },
  });

  // info about selected chat
  const [inputValue, setInputValue] = useState("");
  const [currentChatTitle, setCurrentChatTitle] = useState("");
  const [currentChatId, setCurrentChatId] = useState("");
  const [currentChatAvatar, setCurrentChatAvatar] = useState("");
  const [currentChatIsIndividual, setCurrentChatIsIndividual] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [tokenError, setTokenError] = useState(false);
  const [pinnedMessage, setPinnedMessage] = useState<Message | null>(null);
  const [pinnedMessages, setPinnedMessages] = useState<Message[]>([]);
  const [currentPinnedIndex, setCurrentPinnedIndex] = useState(0);

  // Состояние для хранения информации о сообщении, на которое отвечаем
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);

  // Состояние для контекстного меню
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    messageId: string;
    isPinned: boolean;
  }>({
    visible: false,
    x: 0,
    y: 0,
    messageId: "",
    isPinned: false,
  });

  const individualChatMessageStore = useStore(
    ChatModel.$individualChatMessageStore
  );
  const getIndividualChatMessage = useEvent(
    ChatModel.getIndividualChatMessageFx
  );
  const pinnedMessagesStore = useStore(ChatModel.$pinnedMessagesStore);
  const getPinnedMessages = useEvent(ChatModel.getPinnedMessagesFx);
  const setCurrentSoloChat = useEvent(ChatModel.setCurrentSoloChatEv);
  const currentSoloChat = useStore(ChatModel.$currentSoloChat);
  const isLoading = useStore(ChatModel.getIndividualChatsListFx.pending);
  const individualChatsList = useStore(ChatModel.$individualChatsListStore);
  const groupChatsList = useStore(ChatModel.$groupChatsListStore);
  const accountId = useStore(AccountModel.$accountInfo)?.account.account.id;
  const logout = useEvent(userLogoutEvent);
  const getIndividualChatsList = useEvent(ChatModel.getIndividualChatsListFx);
  const getGroupChatsList = useEvent(ChatModel.getGroupChatsListFx);

  const {
    sendMessage,
    deleteMessage,
    readMessages,
    pinMessage,
    unpinMessage,
    isConnected,
    isError,
  } = useChatListWebSocket();

  const messageListRef = useRef<HTMLDivElement>(null);

  // Состояние для отслеживания изменений в закрепленных сообщениях
  const [pinnedUpdateTrigger, setPinnedUpdateTrigger] = useState(0);

  // Функция для принудительного обновления компонента после изменения закрепленных сообщений
  const forceUpdatePinnedMessages = () => {
    setPinnedUpdateTrigger((prev) => prev + 1);
  };

  // Обработка ошибки с токеном
  useEffect(() => {
    if (isError === "invalid_token") {
      console.error("Ошибка аутентификации WebSocket: недействительный токен");
      setTokenError(true);

      // Опционально: можно автоматически разлогинить пользователя
      // logout();
    }
  }, [isError, logout]);

  // Закрытие контекстного меню при клике вне его
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [contextMenu]);

  // Загружаем списки чатов при инициализации
  useEffect(() => {
    ChatModel.getIndividualChatsListFx();
    ChatModel.getGroupChatsListFx();
  }, []);

  // Обновляем закрепленное сообщение при изменении списка сообщений
  useEffect(() => {
    if (individualChatMessageStore?.messages) {
      // Ищем закрепленное сообщение
      const pinned = individualChatMessageStore.messages.find(
        (msg) => msg.is_pinned
      );
      setPinnedMessage(pinned || null);
    } else {
      setPinnedMessage(null);
    }
  }, [individualChatMessageStore?.messages]);

  // Обновляем закрепленные сообщения при изменении списка сообщений или при получении данных от API
  useEffect(() => {
    // Получаем сообщения, помеченные как закрепленные в хранилище сообщений
    const pinnedFromMessages =
      individualChatMessageStore?.messages.filter((msg) => msg.is_pinned) || [];

    // Получаем сообщения из специального хранилища закрепленных сообщений
    const pinnedFromStore = pinnedMessagesStore?.messages || [];

    // Получаем IDs закрепленных сообщений из attached
    const attachedIds = individualChatMessageStore?.attached || "";
    const attachedArray = Array.isArray(attachedIds)
      ? attachedIds
      : typeof attachedIds === "string" && attachedIds
        ? [attachedIds]
        : [];

    // Находим сообщения, которые указаны в attached, но еще не помечены как закрепленные
    const attachedMessages =
      individualChatMessageStore?.messages
        .filter((msg) => attachedArray.includes(msg.id) && !msg.is_pinned)
        .map((msg) => ({ ...msg, is_pinned: true })) || [];

    // Объединяем все источники закрепленных сообщений
    const allPinned = [
      ...pinnedFromMessages,
      ...pinnedFromStore,
      ...attachedMessages,
    ];

    // Удаляем дубликаты по ID
    const uniquePinned = allPinned.filter(
      (msg, index, self) => index === self.findIndex((m) => m.id === msg.id)
    );

    setPinnedMessages(uniquePinned);

    // Если есть закрепленные сообщения, устанавливаем первое из них как текущее
    if (uniquePinned.length > 0) {
      setPinnedMessage(uniquePinned[0]);
      setCurrentPinnedIndex(0);
    } else {
      setPinnedMessage(null);
      setCurrentPinnedIndex(0);
    }

    // Добавляем логирование для отладки
    console.log("Закрепленные сообщения обновлены:", {
      pinnedFromMessages: pinnedFromMessages.length,
      pinnedFromStore: pinnedFromStore.length,
      attachedIds: attachedArray,
      attachedMessages: attachedMessages.length,
      totalUnique: uniquePinned.length,
    });
  }, [
    individualChatMessageStore?.messages,
    individualChatMessageStore?.attached,
    pinnedMessagesStore?.messages,
  ]);

  // Функция для перехода к предыдущему закрепленному сообщению
  const goToPreviousPinnedMessage = (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем всплытие события
    if (pinnedMessages.length > 1) {
      const newIndex =
        (currentPinnedIndex - 1 + pinnedMessages.length) %
        pinnedMessages.length;
      setCurrentPinnedIndex(newIndex);
      setPinnedMessage(pinnedMessages[newIndex]);
      scrollToPinnedMessage(pinnedMessages[newIndex].id);
    }
  };

  // Функция для перехода к следующему закрепленному сообщению
  const goToNextPinnedMessage = (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем всплытие события
    if (pinnedMessages.length > 1) {
      const newIndex = (currentPinnedIndex + 1) % pinnedMessages.length;
      setCurrentPinnedIndex(newIndex);
      setPinnedMessage(pinnedMessages[newIndex]);
      scrollToPinnedMessage(pinnedMessages[newIndex].id);
    }
  };

  // Прокрутка к закрепленному сообщению
  const scrollToPinnedMessage = (messageId?: string) => {
    if (!pinnedMessage && !messageId) return;

    const id = messageId || pinnedMessage?.id;
    if (!id) return;

    const messageElement = document.getElementById(`message-${id}`);
    if (messageElement && messageListRef.current) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      // Добавляем подсветку для закрепленного сообщения
      messageElement.classList.add("highlight-pinned");
      setTimeout(() => {
        messageElement.classList.remove("highlight-pinned");
      }, 2000);
    }
  };

  // Функция для прокрутки к сообщению, на которое ответили
  const scrollToReplyMessage = (messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement && messageListRef.current) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      // Добавляем подсветку для сообщения, на которое ответили
      messageElement.classList.add("highlight-reply");
      setTimeout(() => {
        messageElement.classList.remove("highlight-reply");
      }, 2000);
    }
  };

  // Отрисовка сообщений
  const renderMessages = (messages: Message[]) => {
    return messages.map((message, index) => {
      // Check if this message is the first in a sequence from the same sender
      const isFirstInSequence =
        index === 0 || messages[index - 1].sender_id !== message.sender_id;

      // Only show title (sender name) for the first message in a sequence
      let title;
      let profession;

      // Determine sender info
      if (message.sender_id === accountId) {
        title = "Вы";
      } else if (currentSoloChat && currentSoloChat.id_group_chat) {
        // For group chats
        // Find the participant in the group chat
        const groupChat = currentSoloChat as GroupChatList;
        const participant = groupChat.participant;
        if (participant) {
          title = `${participant.first_name || ""} ${
            participant.last_name || ""
          }`.trim();
          // Only set profession if it exists and is not empty
          if (participant.profession && participant.profession.trim() !== "") {
            profession = participant.profession;
          }
        } else {
          title = "Участник чата"; // Default name for group chat participants
        }
      } else {
        // For individual chats - use type assertion to handle the union type
        const individualChat = currentSoloChat as IndividualChat;
        if (individualChat?.participant_2) {
          title = `${individualChat.participant_2.first_name || ""} ${
            individualChat.participant_2.last_name || ""
          }`.trim();
          // Only set profession if it exists and is not empty
          if (
            individualChat.participant_2.profession &&
            individualChat.participant_2.profession.trim() !== ""
          ) {
            profession = individualChat.participant_2.profession;
          }
        }
      }

      // Создаем уникальный ключ для каждого сообщения, используя id или комбинацию id и индекса
      const uniqueKey = message.id
        ? `message-${message.id}-${pinnedUpdateTrigger}`
        : `message-${index}-${message.created_at}-${pinnedUpdateTrigger}`;

      // Проверяем, закреплено ли сообщение
      // Используем свойство is_pinned из самого сообщения или проверяем наличие ID в массиве attached
      const isPinned =
        !!message.is_pinned ||
        !!(
          individualChatMessageStore?.attached &&
          ((Array.isArray(individualChatMessageStore.attached) &&
            individualChatMessageStore.attached.includes(message.id)) ||
            (typeof individualChatMessageStore.attached === "string" &&
              individualChatMessageStore.attached === message.id))
        );

      // Only show avatar for the first message in a sequence from the same sender
      const showAvatar = isFirstInSequence && message.sender_id !== accountId;

      // Get avatar URL only if we need to show it
      const avatarUrl = showAvatar
        ? getAvatarResourcesUrl(
            currentSoloChat && currentSoloChat.id_group_chat
              ? //@ts-ignore
                (currentSoloChat as GroupChatList).participant.avatar ?? ""
              : //@ts-ignore
                currentSoloChat.participant_1.avatar ?? ""
          )
        : undefined;

      // Обрабатываем сообщение-ответ, если оно есть
      let replyMessage = undefined;
      if (message.reply) {
        // Ищем сообщение, на которое ответили
        let replyMessageId =
          typeof message.reply === "string" ? message.reply : message.reply.id;
        const replyToMessage = individualChatMessageStore?.messages.find(
          (msg) => msg.id === replyMessageId
        );

        if (replyToMessage) {
          // Определяем имя отправителя сообщения, на которое ответили
          let senderName = "";
          let profession = "";

          if (replyToMessage.sender_id === accountId) {
            senderName = "Вы";
          } else {
            // Используем информацию о отправителе из самого сообщения, если она есть
            if (replyToMessage.sender_name) {
              senderName = replyToMessage.sender_name;
              if (replyToMessage.sender_surname) {
                senderName += " " + replyToMessage.sender_surname;
              }
              profession = replyToMessage.sender_profession || "";
            } else if (currentSoloChat?.id_group_chat) {
              // Групповой чат
              const groupChat = currentSoloChat as GroupChatList;
              if (groupChat.participant) {
                senderName = `${groupChat.participant.first_name || ""} ${
                  groupChat.participant.last_name || ""
                }`.trim();
                profession = groupChat.participant.profession || "";
              } else {
                senderName = "Участник чата";
              }
            } else {
              // Личный чат
              const individualChat = currentSoloChat as IndividualChat;
              if (individualChat?.participant_2) {
                senderName = `${
                  individualChat.participant_2.first_name || ""
                } ${individualChat.participant_2.last_name || ""}`.trim();
                profession = individualChat.participant_2.profession || "";
              } else {
                senderName = "Собеседник";
              }
            }
          }

          replyMessage = {
            text: replyToMessage.text,
            sender: senderName,
            id: replyToMessage.id,
            profession: profession,
          };
        } else if (typeof message.reply !== "string" && message.reply.text) {
          // Если сообщение, на которое ответили, не найдено в хранилище, но у нас есть его данные
          replyMessage = {
            text: message.reply.text,
            sender: message.reply.sender_name || "Неизвестный",
            id: message.reply.id || "",
            profession: message.reply.sender_profession || "",
          };
        }
      }

      return (
        <div
          key={uniqueKey}
          id={`message-${message.id}`}
          onContextMenu={(e) => handleContextMenu(e, message)}
          className={`relative ${isPinned ? "pinned-message" : ""}`}
        >
          <ChatMessage
            title={isFirstInSequence ? title : undefined}
            message={message.text}
            time={message.created_at.slice(11, 16)}
            icon={avatarUrl}
            isYoursMessage={message.sender_id === accountId}
            delivered
            isPinned={isPinned}
            profession={
              isFirstInSequence && profession ? profession : undefined
            }
            replyMessage={replyMessage}
            onReplyClick={scrollToReplyMessage}
          />
        </div>
      );
    });
  };

  // Отрисовка разделителя даты
  const renderDateSeparator = (dateString: string) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const monthNames = [
      "января",
      "февраля",
      "марта",
      "апреля",
      "мая",
      "июня",
      "июля",
      "августа",
      "сентября",
      "октября",
      "ноября",
      "декабря",
    ];
    const monthIndex = date.getMonth();
    const formattedDate = `${day} ${monthNames[monthIndex]}`;

    return (
      <div key={`date-${dateString}`} className="text-center my-2">
        {formattedDate === "NaN undefined" ? "" : formattedDate}
      </div>
    );
  };

  // отправка сообщения
  const handleSendMessage = () => {
    if (!inputValue.trim()) return; // Не отправляем пустые сообщения

    // Если есть ошибка с токеном, показываем сообщение и не отправляем
    if (tokenError) {
      console.error(
        "Невозможно отправить сообщение: проблема с аутентификацией"
      );
      alert("Ошибка аутентификации. Пожалуйста, войдите в систему заново.");
      return;
    }

    console.log("Отправляемое сообщение:", inputValue);

    if (currentSoloChat) {
      try {
        if (currentSoloChat.id_group_chat) {
          sendMessage(
            inputValue,
            currentSoloChat.id_group_chat,
            "group",
            "message",
            replyToMessage?.id
          );
          console.log(
            "Сообщение отправлено в групповой чат:",
            currentSoloChat.id_group_chat
          );
        } else {
          sendMessage(
            inputValue,
            currentSoloChat.id,
            "solo",
            "message",
            replyToMessage?.id
          );
          console.log("Сообщение отправлено в личный чат:", currentSoloChat.id);
        }

        // Добавляем сообщение локально для мгновенного отображения
        const newMessage = {
          id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Генерируем гарантированно уникальный ID
          chat_id: currentSoloChat.id_group_chat || currentSoloChat.id,
          sender_id: accountId ?? "",
          text: inputValue,
          created_at: new Date().toISOString(),
          reply: replyToMessage?.id, // Используем только ID сообщения, на которое отвечаем
        };

        console.log("Добавляем сообщение в локальный стор:", newMessage);
        addMessage(newMessage as Message);

        // Очищаем поле ввода и информацию о сообщении, на которое отвечаем
        setInputValue("");
        setReplyToMessage(null);
      } catch (error) {
        console.error("Ошибка при отправке сообщения:", error);
      }
    } else {
      console.error("Невозможно отправить сообщение: чат не выбран");
    }
  };

  // Фильтрация сообщений
  const filterMessages = (messages: Message[], searchText: string) => {
    return messages.filter((message) =>
      message.text.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const handleSearchInputChange = (value: string) => {
    setSearchText(value);
  };

  const filteredMessages = filterMessages(
    individualChatMessageStore?.messages.slice().reverse() || [],
    searchText
  );

  // Обработчик выбора индивидуального чата
  const handleSelectIndividualChat = (chat: IndividualChat) => {
    console.log("Выбран индивидуальный чат:", chat);
    setCurrentSoloChat(chat);
    setCurrentChatId(chat.id);
    setCurrentChatTitle(
      `${chat.participant_2.first_name || ""} ${
        chat.participant_2.last_name || ""
      }`.trim() || "Без имени"
    );
    setCurrentChatAvatar(chat.participant_2.avatar || "");
    setCurrentChatIsIndividual(true);

    // Отмечаем все сообщения в чате как прочитанные
    try {
      readMessages(chat.id, "solo");
      console.log("Отправлен запрос на прочтение сообщений в чате:", chat.id);

      // Обновляем список чатов, чтобы отобразить изменение счетчика непрочитанных сообщений
      setTimeout(() => {
        getIndividualChatsList();
      }, 300);
    } catch (error) {
      console.error("Ошибка при отметке сообщений как прочитанные:", error);
    }

    getIndividualChatMessage({
      chat_id: chat.id,
      limit: "100",
      offset: "0",
      type_chat: "solo",
    });
  };

  // Обработчик выбора группового чата
  const handleSelectGroupChat = (chat: GroupChatList) => {
    console.log("Выбран групповой чат:", chat);
    setCurrentSoloChat(chat);
    setCurrentChatId(chat.id_group_chat);
    setCurrentChatTitle(chat.group_chat.name || "Групповой чат");
    setCurrentChatAvatar(chat.group_chat.avatar || "");
    setCurrentChatIsIndividual(false);

    // Отмечаем все сообщения в групповом чате как прочитанные
    try {
      readMessages(chat.id_group_chat, "group");
      console.log(
        "Отправлен запрос на прочтение сообщений в групповом чате:",
        chat.id_group_chat
      );

      // Обновляем список групповых чатов, чтобы отобразить изменение счетчика непрочитанных сообщений
      setTimeout(() => {
        getGroupChatsList();
      }, 300);
    } catch (error) {
      console.error(
        "Ошибка при отметке сообщений как прочитанные в групповом чате:",
        error
      );
    }

    getIndividualChatMessage({
      chat_id: chat.id_group_chat,
      limit: "100",
      offset: "0",
      type_chat: "group",
    });
  };

  // Обработчик правого клика на сообщение для вызова контекстного меню
  const handleContextMenu = (e: React.MouseEvent, message: Message) => {
    e.preventDefault(); // Предотвращаем появление стандартного контекстного меню браузера

    // Проверяем, закреплено ли сообщение
    const isPinned =
      !!message.is_pinned ||
      !!(
        individualChatMessageStore?.attached &&
        ((Array.isArray(individualChatMessageStore.attached) &&
          individualChatMessageStore.attached.includes(message.id)) ||
          (typeof individualChatMessageStore.attached === "string" &&
            individualChatMessageStore.attached === message.id))
      );

    // Устанавливаем данные для контекстного меню
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      messageId: message.id,
      isPinned,
    });
  };

  // Обработчик для ответа на сообщение
  const handleReply = (messageId: string) => {
    // Закрываем контекстное меню
    setContextMenu({ ...contextMenu, visible: false });

    // Находим сообщение по ID
    const message = individualChatMessageStore?.messages.find(
      (msg) => msg.id === messageId
    );

    if (message) {
      // Устанавливаем сообщение, на которое отвечаем
      setReplyToMessage(message);

      // Фокусируемся на поле ввода
      setTimeout(() => {
        const inputField = document.querySelector(
          'input[name="messageText"]'
        ) as HTMLInputElement;
        if (inputField) {
          inputField.focus();
        }
      }, 100);
    }
  };

  // Обработчик для закрепления сообщения
  const handlePin = (messageId: string) => {
    // Закрываем контекстное меню
    setContextMenu({ ...contextMenu, visible: false });

    if (currentSoloChat) {
      try {
        const chatId = currentSoloChat.id_group_chat || currentSoloChat.id;
        const chatType = currentSoloChat.id_group_chat ? "group" : "solo";

        // Вызываем функцию для закрепления сообщения
        pinMessage(messageId, chatId, chatType);

        // Принудительно обновляем компонент для отображения закрепленных сообщений
        forceUpdatePinnedMessages();

        console.log("Сообщение закреплено:", messageId);
      } catch (error) {
        console.error("Ошибка при закреплении сообщения:", error);
      }
    }
  };

  // Обработчик для открепления сообщения
  const handleUnpin = (messageId: string) => {
    // Закрываем контекстное меню
    setContextMenu({ ...contextMenu, visible: false });

    if (currentSoloChat) {
      try {
        const chatId = currentSoloChat.id_group_chat || currentSoloChat.id;
        const chatType = currentSoloChat.id_group_chat ? "group" : "solo";

        // Вызываем функцию для открепления сообщения
        unpinMessage(messageId, chatId, chatType);

        // Принудительно обновляем компонент для отображения закрепленных сообщений
        forceUpdatePinnedMessages();

        console.log("Сообщение откреплено:", messageId);
      } catch (error) {
        console.error("Ошибка при откреплении сообщения:", error);
      }
    }
  };

  // Обработчик для удаления сообщения
  const handleDelete = (messageId: string) => {
    // Закрываем контекстное меню
    setContextMenu({ ...contextMenu, visible: false });

    if (currentSoloChat) {
      try {
        const chatType = currentSoloChat.id_group_chat ? "group" : "solo";

        // Вызываем функцию для удаления сообщения
        deleteMessage(messageId, chatType);

        console.log("Сообщение удалено:", messageId);
      } catch (error) {
        console.error("Ошибка при удалении сообщения:", error);
      }
    }
  };

  // Функция для группировки сообщений по дате
  const groupMessagesByDate = (messages: Message[]) => {
    const groupedMessages: Record<string, Message[]> = {};

    messages.forEach((message) => {
      // Получаем дату из строки даты создания сообщения
      const date = message.created_at.split("T")[0]; // Формат "2023-05-20"

      // Если для этой даты еще нет массива сообщений, создаем его
      if (!groupedMessages[date]) {
        groupedMessages[date] = [];
      }

      // Добавляем сообщение в соответствующую группу
      groupedMessages[date].push(message);
    });

    return groupedMessages;
  };

  return (
    <div className="fixed top-0 left-0 w-full h-full overflow-auto bg-white bg-opacity-10 backdrop-filter backdrop-blur-[3px] flex justify-end items-start z-10 p-8">
      {tokenError && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-md shadow-md">
          Ошибка аутентификации. Пожалуйста, войдите в систему заново.
          <button
            className="ml-4 bg-white text-red-500 px-2 py-1 rounded-md text-sm"
            onClick={() => logout()}
          >
            Выйти
          </button>
        </div>
      )}

      {/* Контекстное меню */}
      {contextMenu.visible && (
        <div
          className="fixed bg-blue-50 rounded-lg shadow-lg z-50 py-1"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
            minWidth: "180px",
            maxWidth: "250px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-4 py-2 hover:bg-blue-100 text-sm"
            onClick={() => handleReply(contextMenu.messageId)}
          >
            Ответить
          </button>
          {contextMenu.isPinned ? (
            <button
              className="w-full text-left px-4 py-2 hover:bg-blue-100 text-sm"
              onClick={() => handleUnpin(contextMenu.messageId)}
            >
              Открепить
            </button>
          ) : (
            <button
              className="w-full text-left px-4 py-2 hover:bg-blue-100 text-sm"
              onClick={() => handlePin(contextMenu.messageId)}
            >
              Закрепить в чате
            </button>
          )}
          <button
            className="w-full text-left px-4 py-2 hover:bg-blue-100 text-sm text-red-500"
            onClick={() => handleDelete(contextMenu.messageId)}
          >
            Удалить
          </button>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        className="shadow-sm flex flex-row gap-[4px] bg-[#F5F8FE] p-[4px] w-[800px] h-full rounded-[8px] justify-between"
      >
        {isLoading ? (
          <div className="flex items-center justify-center w-full h-full">
            <BarLoader color="#36D7B7" width={"40%"} loading />
          </div>
        ) : (
          <>
            <div className="flex flex-col w-[300px]">
              <SearchInput
                placeholder="Поиск"
                onInputChange={handleSearchInputChange}
              />
              <GroupChatGroups
                title="Групповые чаты"
                groups={groupChatsList?.chats.map((chat) => (
                  <ChatGrop
                    key={chat.id}
                    onClick={() => {
                      handleSelectGroupChat(chat);
                    }}
                    title={chat.group_chat.name || "---"}
                    subTitle={chat.last_message?.text || "Напишите что-нибудь"}
                    img={getAvatarResourcesUrl(chat.group_chat.avatar)}
                    countNewMessage={chat.unread_messages}
                    sender={
                      chat.last_message
                        ? chat.last_message.sender_id === accountId
                          ? "Вы"
                          : `${chat.participant.first_name}:`
                        : ""
                    }
                  />
                ))}
              />
              <ChatGroups
                title="Чаты"
                groups={individualChatsList?.chats.map((chat) => (
                  <ChatGrop
                    key={chat.id}
                    onClick={() => handleSelectIndividualChat(chat)}
                    title={`${chat.participant_2.first_name || ""} ${
                      chat.participant_2.last_name || ""
                    }`}
                    subTitle={chat.last_message?.text || "Напишите что-нибудь"}
                    img={getAvatarResourcesUrl(chat.participant_2.avatar)}
                    countNewMessage={chat.unread_messages}
                    sender={
                      chat.last_message
                        ? chat.last_message.sender_id === accountId
                          ? "Вы"
                          : `${chat.participant_2.first_name || ""} ${
                              chat.participant_2.last_name || ""
                            }:`
                        : ""
                    }
                  />
                ))}
              />
            </div>

            <div className="flex flex-col w-[427px]">
              <SettingsPanel
                title={currentChatTitle}
                setVisible={setVisible}
                chatId={currentChatId}
                chatAvatar={currentChatAvatar}
                isIndividualChat={currentChatIsIndividual}
              />

              {/* Отображение закрепленного сообщения */}
              {pinnedMessage && (
                <div className="bg-blue-50 p-2 mx-2 mb-2 rounded-lg flex items-center">
                  <div
                    className="flex items-center cursor-pointer flex-1"
                    onClick={() => scrollToPinnedMessage()}
                  >
                    <img src={pinIcon} alt="pin" className="w-4 h-4 mr-2" />
                    <div className="flex-1 text-sm truncate">
                      {pinnedMessage.text}
                    </div>
                    {pinnedMessages.length > 0 && (
                      <div className="text-xs text-gray-500 ml-1">
                        {currentPinnedIndex + 1}/{pinnedMessages.length}
                      </div>
                    )}
                  </div>

                  {pinnedMessages.length > 1 && (
                    <div className="flex ml-2">
                      <button
                        onClick={goToPreviousPinnedMessage}
                        className="p-1 hover:bg-blue-100 rounded-full"
                      >
                        <img
                          src={arrowUpIcon}
                          alt="previous"
                          className="w-3 h-3"
                        />
                      </button>
                      <button
                        onClick={goToNextPinnedMessage}
                        className="p-1 hover:bg-blue-100 rounded-full transform rotate-[-90deg]"
                      >
                        <img
                          src={arrowDownIcon}
                          alt="next"
                          className="w-3 h-3"
                        />
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div
                ref={messageListRef}
                className="overflow-auto px-[12px]"
                style={{ height: "calc(100vh - 100px)" }}
              >
                {Object.entries(groupMessagesByDate(filteredMessages)).map(
                  ([date, messages]) => (
                    <React.Fragment key={date}>
                      {renderDateSeparator(date)}
                      {renderMessages(messages)}
                    </React.Fragment>
                  )
                )}
              </div>

              {/* Отображение интерфейса ответа на сообщение */}
              {replyToMessage && (
                <div className="bg-blue-50 p-2 mx-2 mb-2 rounded-lg flex items-center">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div className="w-1 h-12 bg-blue-400 rounded-full mr-2"></div>
                      <div className="flex-1">
                        <div className="text-xs text-blue-500 font-medium">
                          {replyToMessage.sender_id === accountId
                            ? "Вы"
                            : currentSoloChat?.id_group_chat
                              ? (currentSoloChat as GroupChatList).participant
                                  ?.first_name || "Участник чата"
                              : (currentSoloChat as IndividualChat)
                                  ?.participant_2?.first_name || "Собеседник"}
                        </div>
                        <div className="text-xs text-gray-600 truncate max-w-[280px]">
                          {replyToMessage.text}
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    className="ml-2 text-gray-500 hover:text-gray-700 p-1"
                    onClick={() => setReplyToMessage(null)}
                  >
                    ✕
                  </button>
                </div>
              )}

              <BaseInput
                name={"messageText"}
                placeholder="Написать сообщение"
                renderRightIcon={inputValue === "" ? sendIcon : sendActiveIcon}
                control={control}
                onInputChange={() => {}}
                value={inputValue}
                onClickRightIcon={handleSendMessage}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </div>
          </>
        )}
      </form>

      {/* Стили для подсветки закрепленного сообщения */}
      <style>{`
        .pinned-message {
          border-left: 3px solid #0767FD;
        }
        .highlight-pinned {
          background-color: rgba(7, 103, 253, 0.1);
          transition: background-color 0.5s ease;
        }
        .highlight-reply {
          background-color: rgba(7, 103, 253, 0.1);
          transition: background-color 0.5s ease;
        }
      `}</style>
    </div>
  );
};

export { ChatFeature };
